// =============================================================================
// features/sales/agenda/AgendaIndicators.tsx — Faixa de indicadores
// (vencidos, agora, hoje, próximos 7 dias — doc 26 §6.1).
//
// Mobile: rolagem horizontal (doc 26 §6.1 "Estrutura móvel"). Desktop: grid
// de 4 colunas. Cada indicador é um GET independente com limit=1 (só o
// campo `total` importa) — 4 chamadas leves em paralelo, não a lista inteira
// 4 vezes (useAppointmentsCount, hooks/sales/useAppointments.ts).
// =============================================================================

import * as React from 'react';

import { Stat } from '../../../components/ui/Stat';
import { useAppointmentsCount } from '../../../hooks/sales/useAppointments';

import {
  PROXIMOS_INDICATOR_DAYS,
  agoraWindow,
  hojeWindow,
  proximosIndicatorWindow,
  vencidosWindow,
} from './buckets';

export interface AgendaIndicatorsProps {
  now: Date;
  enabled: boolean;
}

export function AgendaIndicators({ now, enabled }: AgendaIndicatorsProps): React.JSX.Element {
  const vencidos = useAppointmentsCount(vencidosWindow(now), { enabled });
  const agora = useAppointmentsCount(agoraWindow(now), { enabled });
  const hoje = useAppointmentsCount(hojeWindow(now), { enabled });
  const proximos = useAppointmentsCount(proximosIndicatorWindow(now), { enabled });

  const items: { label: string; value: number | undefined; description: string }[] = [
    { label: 'Vencidos', value: vencidos.total, description: 'Precisam de retomada' },
    { label: 'Agora', value: agora.total, description: 'Próxima 1 hora + atrasados' },
    { label: 'Hoje', value: hoje.total, description: 'Todo o dia de hoje' },
    {
      label: 'Próximos 7 dias',
      value: proximos.total,
      description: `A partir de amanhã (${PROXIMOS_INDICATOR_DAYS}d)`,
    },
  ];

  return (
    <div
      className="flex gap-4 overflow-x-auto snap-x pb-1 sm:pb-0 sm:grid sm:grid-cols-4 sm:overflow-visible"
      role="group"
      aria-label="Indicadores da agenda"
    >
      {items.map((item) => (
        <div key={item.label} className="min-w-[200px] sm:min-w-0 snap-start shrink-0 sm:shrink">
          <Stat label={item.label} value={item.value ?? '—'} description={item.description} />
        </div>
      ))}
    </div>
  );
}
